import ReactEChartsCore from '../EChart';
import * as echarts from 'echarts/core';
import { useAppContext } from '../../../providers/AppProvider';
import { TooltipComponent, GridComponent } from 'echarts/components';
import { BarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { Spinner, Alert } from 'react-bootstrap';
import { useRemainingMaterialSummary } from '../../../features/jobs/hooks/useProductionDashboardStats';

echarts.use([TooltipComponent, GridComponent, BarChart, CanvasRenderer]);

const prettyPlasticLabel = item => {
  const material = String(item?.material || '')
    .trim()
    .toUpperCase();

  // BLR: keep the spec ("15 x 0.15") as-is (don't upper-case the x)
  if (material === 'BLR' || material === 'STEEL') {
    const spec = String(item?.grade || '').trim();
    return spec ? `BLR ${spec}` : 'BLR armouring';
  }

  const grade = String(item?.grade || '')
    .trim()
    .toUpperCase();

  if (!material && !grade) return 'UNKNOWN';
  if (!material) return grade;
  if (!grade) return material;

  return `${material} ${grade}`;
};

const getDefaultOptions = (getThemeColor, chartData) => ({
  color: [
    getThemeColor('gray-400'),
    getThemeColor('success'),
    getThemeColor('info'),
    getThemeColor('warning')
  ],
  tooltip: {
    trigger: 'axis',
    padding: [7, 10],
    backgroundColor: getThemeColor('gray-soft'),
    borderColor: getThemeColor('gray-200'),
    axisPointer: {
      shadowStyle: {
        color: 'red'
      }
    },
    formatter: params => {
      const item = Array.isArray(params) ? params[0] : params;
      if (!item) return '';

      return `
        <strong>${item.axisValue}</strong><br/>
        ${item.marker} Needed: <strong>${Number(item.value || 0).toLocaleString(
          'ro-RO',
          {
            minimumFractionDigits: 0,
            maximumFractionDigits: 3
          }
        )} kg</strong>
      `;
    }
  },
  xAxis: {
    type: 'category',
    axisLabel: {
      color: getThemeColor('gray-800'),
      interval: 0,
      fontFamily: 'Nunito Sans',
      fontWeight: 600,
      fontSize: 12.8
    },
    data: chartData.map(item => prettyPlasticLabel(item)),
    axisLine: {
      lineStyle: {
        color: getThemeColor('gray-300')
      }
    },
    axisTick: false
  },
  yAxis: {
    show: true,
    type: 'value',
    axisLine: {
      lineStyle: {
        type: 'solid',
        color: getThemeColor('gray-300')
      }
    },
    axisLabel: {
      color: getThemeColor('gray-900'),
      margin: 20,
      fontSize: 12.8,
      interval: 0,
      formatter: value => Number(value).toLocaleString('ro-RO')
    },
    splitLine: {
      show: true,
      lineStyle: {
        color: getThemeColor('gray-300'),
        type: 'solid'
      }
    },
    axisTick: {
      show: true,
      length: 15,
      alignWithLabel: true,
      lineStyle: {
        color: getThemeColor('gray-300')
      }
    }
  },
  series: [
    {
      name: 'Needed',
      type: 'bar',
      barWidth: '12px',
      data: chartData.map(item => Number(item.planned_kg || 0)),
      label: 'TEST',
      barGap: '30%',
      label: { show: false },
      itemStyle: {
        borderRadius: [2, 2, 0, 0],
        color: getThemeColor('primary')
      }
    }
  ],
  grid: {
    right: 5,
    left: 0,
    bottom: '15%',
    top: 20,
    containLabel: true
  }
});

const ProductionOrdersPvcQty = () => {
  const { getThemeColor } = useAppContext();
  const { data, isLoading, isError, error } = useRemainingMaterialSummary();

  const chartData = [
    ...(data?.pvcByGrade || []),
    ...(data?.blrByGrade || [])
  ];

  if (isLoading) {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ minHeight: '350px', width: '100%' }}
      >
        <Spinner animation="border" size="sm" className="me-2" />
        <span>Loading chart...</span>
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="danger" className="mb-0">
        Failed to load plastic chart: {error?.message || 'Unknown error'}
      </Alert>
    );
  }

  if (!chartData.length) {
    return (
      <div
        className="d-flex align-items-center justify-content-center text-700"
        style={{ minHeight: '350px', width: '100%' }}
      >
        No plastic requirement found
      </div>
    );
  }

  return (
    <ReactEChartsCore
      echarts={echarts}
      option={getDefaultOptions(getThemeColor, chartData)}
      style={{ minHeight: '350px', width: '100%' }}
      notMerge={true}
      lazyUpdate={true}
    />
  );
};

export default ProductionOrdersPvcQty;
